import { type FC, useCallback, useState } from "react";
import { css } from "@emotion/css";
import styled from "@emotion/styled";
import { Checkbox, DropdownInput, Input, Modal, type ModalProps, Option } from "@snapchat/snap-design-system";
import { StatusType } from "@snapchat/snap-design-system/lib/components/Control/types";
import { type MyLensesAppVersion } from "../../../gql-generated/types";
import { useApps } from "../hooks/useApps";

// Validation rules set via snap-canvas-web/snap-connect-developer-portal
const reservedNameExclusions = /^(?!.*snapchat|.*snap inc|.*bitmoji).*/i;
const minLength = 3;
const maxLength = 100;

const schema = [
    {
        test: (value: string) => value.length < minLength || value.length > maxLength,
        error: `App Name is too short (must be between ${minLength} and ${maxLength} characters)`,
    },
    {
        test: (value: string) => !reservedNameExclusions.test(value),
        error: "App Name is not formatted correctly",
    },
];

interface NewVersionModalProps extends Omit<ModalProps, "children"> {
    handleNewVersion: (version: number) => void;
    existingVersions: readonly MyLensesAppVersion[];
    appId: string;
}

export const NewVersionModal: FC<NewVersionModalProps> = ({
    visible,
    existingVersions,
    appId,
    handleNewVersion,
    onCancel,
}) => {
    const { createVersion, isCreatingVersion } = useApps({ setNewVersionCb: handleNewVersion });
    const [value, setValue] = useState("");
    const [error, setError] = useState("");
    const [selectedVersion, setSelectedVersion] = useState<number | undefined>(undefined);
    const [statusType, setStatusType] = useState<StatusType | undefined>(undefined);
    const [isCloning, setIsCloning] = useState(false);

    const validate = useCallback(
        (value: string) => {
            const error = schema.find((validation) => validation.test(value))?.error ?? "";
            const isDuplicate = existingVersions?.some((version) => version.versionName === value.trim());

            if (error) {
                setError(error);
                setStatusType(StatusType.ERROR);
            }

            if (isDuplicate) {
                setError(`${value} already exists, version names must be unique.`);
                setStatusType(StatusType.ERROR);
            }
            return error;
        },
        [existingVersions],
    );

    const handleInput = useCallback(
        async (value: string) => {
            setError("");
            setStatusType(undefined);
            validate(value);
            setValue(value);
        },
        [validate],
    );

    const handleCheckboxClick = (value: boolean) => {
        setIsCloning(value);
    };

    const onSubmit = useCallback(async () => {
        const error = validate(value);
        const versionTocreate = isCloning && selectedVersion && selectedVersion >= 0 ? selectedVersion : -1;

        if (!error) {
            setStatusType(StatusType.SUCCESS);

            try {
                await createVersion({
                    variables: {
                        appId,
                        sourceVersion: versionTocreate,
                        appVersionName: value,
                    },
                });
                setStatusType(undefined);
                setValue("");
            } catch (e) {
                setStatusType(StatusType.ERROR);
                setError("There was an error processing your request. Please try again");
            }
        }
    }, [validate, value, isCloning, selectedVersion, createVersion, appId]);

    const handleClose = () => {
        setValue("");
        setError("");
        setIsCloning(false);
        setSelectedVersion(undefined);
        onCancel?.();
    };

    return (
        <Modal
            title="New Version"
            visible={visible}
            onAction={onSubmit}
            actionText="Create"
            isActionButtonPending={isCreatingVersion}
            onCancel={handleClose}
            cancelText="Back"
            data-testid="create-version-modal"
            isActionButtonDisabled={!value.length}
            isCancelButtonDisabled={isCreatingVersion}
        >
            <Input
                label="Version Name"
                value={value}
                onChange={handleInput}
                status={error}
                statusType={statusType}
                disabled={isCreatingVersion}
                data-testid="new-version-input"
                autoFocus
                placeholder="Version Name..."
            ></Input>
            <CheckboxWrapper>
                <VerticalTopSeparator />
                <CheckboxContainer>
                    <Checkbox
                        label="Clone Existing Version?"
                        data-testid="clone-version"
                        value={isCloning}
                        onChange={handleCheckboxClick}
                    />
                </CheckboxContainer>
                <VerticalBottomSeparator />
            </CheckboxWrapper>
            <DropdownInput
                disabled={!isCloning}
                className={dropdownStyle}
                defaultValue={selectedVersion}
                onChange={setSelectedVersion}
                data-testid="clone-exisitng-version"
            >
                {existingVersions?.map(({ version, versionName }) => (
                    <Option value={version} key={version}>
                        {versionName}
                    </Option>
                ))}
            </DropdownInput>
        </Modal>
    );
};

const dropdownStyle = css`
    width: 300px;
    padding-left: 40px;
`;

const CheckboxContainer = styled.div`
    margin: 24px 0 8px;
    padding-left: 11px;
`;

const CheckboxWrapper = styled.div`
    position: relative;
`;

const VerticalTopSeparator = styled.span`
    position: absolute;
    top: -20px;
    left: 19px;

    height: 17px;

    border-right: 2px solid lightgray;
`;

const VerticalBottomSeparator = styled.span`
    position: absolute;
    top: 24px;
    left: 19px;

    width: 14px;
    height: 27px;

    border-bottom: 2px solid lightgray;
    border-left: 2px solid lightgray;
`;
